import { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";

import formatDate from "utils/formatDate";
import { TASK_STATUS_OPTIONS_COLOR_MAP } from "static/constants";
import {
  openTaskModal,
  setRefetchTasks,
  setSelectedRowIds,
  setArchiveModalOpen,
  setSingleTaskArchive,
} from "app/store/tasks/slice/taskSlice";
import { showSuccessToast, showErrorToast } from "utils/toast";

import {
  useApproveOrDeclineStatusMutation,
  useSaveTaskMutation,
} from "app/store/tasks/api/taskApi";
import { patientApi } from "app/store/patient/api/patientApi";
import { textFilter } from "utils/table";
import { truncate } from "utils/string";
import {
  ArchiveOutlined,
  CheckCircleOutline,
  TextSnippet,
} from "@mui/icons-material";
import Restore from "@mui/icons-material/Restore";
import { IconButton } from "@mui/material";
import ApproveDeclineConfirmDialog from "./ApproveDeclineConfirmDialog";
import TaskNotesHistoryPopover from "./TaskNotesHistoryPopover";
import { DATE_FORMAT } from "static/date";
import { getFormattedDate } from "utils/date";
import SubmitButton from "components/Button/SubmitButton";
import useNotificationSocket from "containers/AppPage/Header/Notification/useNotificationSocket";
import TaskCommunicationNote from "components/pages/transmissions/modal/patient/tabs/generalInformation/tabs/task/TaskCommunicationNote";

const useTaskTableColumns = (selectedTab, tasks = []) => {
  const dispatch = useDispatch();
  const { emitNewNotification } = useNotificationSocket();
  const [saveTask] = useSaveTaskMutation();
  const [approveOrDeclineStatus] = useApproveOrDeclineStatusMutation();
  const [processingRequest, setProcessingRequest] = useState({
    id: null,
    status: null,
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    rowData: null,
    status: null,
  });
  const [historyAnchor, setHistoryAnchor] = useState(null);
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());

  const handleOpenConfirm = (e, rowData, status) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDialog({ open: true, rowData, status });
  };

  const handleCancelConfirm = () => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  };

  const handleConfirm = async () => {
    const { rowData, status } = confirmDialog;
    setConfirmDialog((prev) => ({ ...prev, open: false }));

    setProcessingRequest({ id: rowData?.id, status });

    try {
      const response = await approveOrDeclineStatus({
        requestId: rowData?.id,
        status,
      }).unwrap();

      if (!response?.error) {
        showSuccessToast(response?.message);
        dispatch(patientApi.util.invalidateTags(["patientNotesHistory"]));
        emitNewNotification([rowData?.requested_by?._id]);
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setProcessingRequest({ id: null, status: null });
    }
  };

  const handleEdit = (rowData) => {
    const selectedTask = Object.assign({}, rowData);
    dispatch(
      openTaskModal({
        selectedTask,
        taskModalOpen: true,
        requestType: "PUT",
        taskModalTitle: "Update Task Information",
      })
    );
  };

  const taskFilterSelectOptions = [
    { value: "", label: "All" },
    { value: "To Do", label: "To Do" },
    { value: "In Progress", label: "In Progress" },
    { value: "Blocked", label: "Blocked" },
    { value: "Complete", label: "Complete" },
  ];

  const markComplete = async (e, rowData) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { message } = await saveTask({
        requestType: "PUT",
        taskId: rowData.id,
        description: rowData.description,
        account: rowData.account,
        status: "Complete",
        patientId: rowData?.patientId,
        patientName: rowData?.patientName,
        patientSerialNumber: rowData?.patientSerialNumber,
      }).unwrap();
      dispatch(setRefetchTasks(true));
      showSuccessToast(message);
    } catch (error) {
      showErrorToast(error?.message || "An error occured, please try later.");
    }
  };

  const archiveTask = (e, rowData) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setSelectedRowIds([rowData.id]));
    dispatch(setArchiveModalOpen(true));
    dispatch(setSingleTaskArchive(true));
  };

  const openPatientModal = (e, rowData) => {
    e.preventDefault();
    e.stopPropagation();

    const url = `/patient/${rowData?.patientId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const isPendingApprovalsTab = "pending-approvals" === selectedTab;

  const textStyle = {
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "14px",
    letterSpacing: 0,
    verticalAlign: "middle",
  };

  const columns = [
    {
      header: "Date",
      accessorKey: "date",
      filterVariant: "date",
      filterFn: textFilter,
      size: 50,
      Cell: ({ row }) => {
        const rowData = row?.original;
        const { created_at } = rowData;
        return <div>{created_at ? formatDate(rowData.created_at) : null}</div>;
      },
    },
    {
      header: "Patient Name",
      accessorKey: "patientName",
      filterVariant: "text",
      filterFn: textFilter,
      size: 80,
      Cell: ({ row }) => {
        const rowData = row?.original;
        if (rowData.patientSerialNumber) {
          return (
            <div
              onClick={(e) => {
                openPatientModal(e, rowData);
              }}
              className="d-flex align-items-center g-1"
            >
              <Tooltip
                title={
                  <div className="transmision_name_tooltip_content">
                    <span
                      className="transmision_name_tooltip_text"
                      style={{ fontSize: "12px", lineHeight: "14px" }}
                    >
                      {rowData?.patientName}
                    </span>
                    <span className="transmision_name_tooltip_text">
                      Click to view patient profile
                    </span>
                  </div>
                }
                placement="bottom"
              >
                <span>{truncate(rowData?.patientName, 18)}</span>
              </Tooltip>
              <Tooltip
                title={
                  <div className="transmision_name_tooltip_content">
                    <span
                      className="transmision_name_tooltip_text"
                      style={{ fontSize: "12px", lineHeight: "14px" }}
                    >
                      {rowData?.patientName}
                    </span>
                    <span className="transmision_name_tooltip_text">
                      Click to view transmission report
                    </span>
                  </div>
                }
                placement="bottom"
              >
                {rowData.reportId && (
                  <Link
                    to={`/patient/report/${rowData.reportId}`}
                    className="d-flex align-items-center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(
                        `/patient/report/${rowData.reportId}`,
                        "_blank"
                      );
                    }}
                  >
                    <TextSnippet sx={{ color: "#757575" }} />
                  </Link>
                )}
              </Tooltip>
            </div>
          );
        }
      },
    },
    {
      header: "Account",
      accessorKey: "account",
      filterVariant: "text",
      filterFn: textFilter,
      size: 100,
    },
    {
      header: isPendingApprovalsTab ? "Note" : "Description",
      accessorKey: "description",
      filterVariant: "text",
      filterFn: textFilter,
      size: 150,
      Cell: ({ row }) => {
        const rowData = row?.original;
        const historyNotes = rowData?.notes ?? [];
        return (
          <div className="d-flex flex-column w-100 g-1">
            <Tooltip
              title={rowData.description}
              placement="bottom"
              PopperProps={{
                className: "tooltip-wrap",
              }}
            >
              <div
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {rowData.description}
              </div>
            </Tooltip>
            {rowData?.note && (
              <>
                <TaskCommunicationNote
                  content={rowData?.note}
                  addedAt={rowData?.noteAddedAt}
                  onExpand={() =>
                    setExpandedRowIds((prev) => new Set([...prev, rowData.id]))
                  }
                  onCollapse={() =>
                    setExpandedRowIds((prev) => {
                      const next = new Set(prev);
                      next.delete(rowData.id);
                      return next;
                    })
                  }
                  action={
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setHistoryAnchor({
                          rowId: rowData.id,
                          el: e.currentTarget,
                        });
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        color: "#94BA33",
                        fontFamily: "Roboto",
                        fontWeight: 500,
                        fontSize: "12px",
                        lineHeight: 1,
                        letterSpacing: 0,
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        verticalAlign: "middle",
                      }}
                    >
                      <Restore style={{ fontSize: "14px" }} />
                      View history ({historyNotes.length})
                    </span>
                  }
                />
                <TaskNotesHistoryPopover
                  anchorEl={historyAnchor?.el}
                  open={historyAnchor?.rowId === rowData.id}
                  onClose={() => setHistoryAnchor(null)}
                  patientName={rowData.patientName}
                  notes={historyNotes}
                />
              </>
            )}
          </div>
        );
      },
    },
    ...(isPendingApprovalsTab
      ? [
          {
            header: "Request From",
            accessorKey: "requested_by",
            filterVariant: "text",
            filterFn: textFilter,
            size: 100,
            Cell: ({ row }) => {
              const rowData = row?.original;
              return <div>{rowData?.requested_by?.name ?? "-"}</div>;
            },
          },
          {
            header: "Request Reason",
            accessorKey: "reason",
            filterVariant: "text",
            filterFn: textFilter,
            size: 120,
            Cell: ({ row }) => {
              const rowData = row?.original;
              return (
                <div>
                  <Tooltip
                    title={rowData.reason}
                    placement="bottom"
                    PopperProps={{
                      className: "tooltip-wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {rowData.reason}
                    </div>
                  </Tooltip>
                </div>
              );
            },
          },
        ]
      : []),
    {
      header: "Status",
      accessorKey: "status",
      filterVariant: "select",
      filterSelectOptions: taskFilterSelectOptions,
      size: 50,
      Cell: ({ row }) => {
        const rowData = row?.original;
        const color = TASK_STATUS_OPTIONS_COLOR_MAP[rowData.status];

        return (
          <span className={`task-status task-status--${color}`}>
            {rowData.status}
          </span>
        );
      },
    },
    {
      header: "Created By",
      accessorKey: "created_by",
      filterVariant: "text",
      size: 100,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return <div>{rowData?.created_by}</div>;
      },
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      filterVariant: "date",
      filterFn: textFilter,
      size: 100,
      Cell: ({ row }) => {
        const rowData = row?.original;
        if (!rowData.created_at) return null;

        return (
          <div className="flex flex-col">
            <p>{getFormattedDate(rowData.created_at)}</p>
            <p>{getFormattedDate(rowData.created_at, DATE_FORMAT.TIME_ONLY)}</p>
          </div>
        );
      },
    },
    {
      accessorKey: "action",
      header: "Action",
      enableColumnFilter: false,
      size: 80,
      Cell: ({ row }) => {
        const rowData = row?.original;
        const isComplete = rowData.status === "Complete";
        const allowedTabsForActionButtons = ["active", "automated", "audited"];
        const isAuditedOrAuditedArchivedTab = [
          "audited",
          "audited-archived",
        ].includes(selectedTab);

        const isRowProcessing = processingRequest.id === rowData.id;
        const isApproving =
          isRowProcessing && processingRequest.status === "approved";
        const isDeclining =
          isRowProcessing && processingRequest.status === "declined";

        const isApprovedOrDeclined = ["approved", "declined"].includes(
          rowData?.status
        );

        return (
          <div style={{ display: "flex" }}>
            {isPendingApprovalsTab && (
              <div className="d-flex g-1 align-items-center">
                {!isApprovedOrDeclined && (
                  <SubmitButton
                    variant="outlined"
                    color="secondary"
                    buttonText="Approve"
                    startIcon={<CheckIcon />}
                    handleClick={(e) =>
                      handleOpenConfirm(e, rowData, "approved")
                    }
                    isLoading={isApproving}
                    disabled={isRowProcessing}
                    sx={{ backgroundColor: "#ffffff", borderRadius: "4px" }}
                  />
                )}
                {!isApprovedOrDeclined && (
                  <SubmitButton
                    variant="outlined"
                    color="error"
                    buttonText="Decline"
                    startIcon={<CloseIcon />}
                    handleClick={(e) =>
                      handleOpenConfirm(e, rowData, "declined")
                    }
                    isLoading={isDeclining}
                    disabled={isRowProcessing}
                    sx={{ backgroundColor: "#ffffff", borderRadius: "4px" }}
                  />
                )}
                {rowData?.status === "approved" && (
                  <div
                    className="d-flex align-items-center g-1"
                    style={{ ...textStyle }}
                  >
                    <CheckIcon fontSize="small" style={{ color: "#94BA33" }} />
                    <p>
                      <span style={{ color: "#94BA33" }}>Approved by </span>
                      <span>
                        {rowData?.reviewed_by?.name} on{" "}
                        {getFormattedDate(rowData?.reviewed_at)}
                      </span>
                    </p>
                  </div>
                )}
                {rowData?.status === "declined" && (
                  <div
                    className="d-flex align-items-center g-1"
                    style={{ ...textStyle }}
                  >
                    <CloseIcon fontSize="small" style={{ color: "#EF5350" }} />
                    <p>
                      <span style={{ color: "#EF5350" }}>Declined by </span>
                      <span>
                        {rowData?.reviewed_by?.name} on{" "}
                        {getFormattedDate(rowData?.reviewed_at)}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}
            {!isAuditedOrAuditedArchivedTab && !isPendingApprovalsTab && (
              <IconButton
                onClick={
                  !isComplete ? (e) => markComplete(e, rowData) : undefined
                }
                color={isComplete ? "secondary" : "primary"}
                sx={{
                  "&:hover": {
                    color: "secondary.main",
                  },
                }}
              >
                <Tooltip
                  title={isComplete ? "Complete" : "Mark Task Complete"}
                  placement="top"
                >
                  <CheckCircleOutline />
                </Tooltip>
              </IconButton>
            )}

            {allowedTabsForActionButtons.includes(selectedTab) && (
              <IconButton
                onClick={(e) => archiveTask(e, rowData)}
                sx={{
                  "&:hover": {
                    color: "secondary.main",
                  },
                }}
              >
                <Tooltip title="Archive Task" placement="top">
                  <ArchiveOutlined />
                </Tooltip>
              </IconButton>
            )}

            {!isAuditedOrAuditedArchivedTab && !isPendingApprovalsTab && (
              <IconButton
                onClick={() => handleEdit(rowData)}
                sx={{
                  "&:hover": {
                    color: "secondary.main",
                  },
                }}
              >
                <Tooltip title="Edit Task" placement="top">
                  <EditIcon data-action="edit-task-icon" />
                </Tooltip>
              </IconButton>
            )}
          </div>
        );
      },
    },
  ];

  const confirmDialogNode = (
    <ApproveDeclineConfirmDialog
      open={confirmDialog.open}
      status={confirmDialog.status}
      onConfirm={handleConfirm}
      onCancel={handleCancelConfirm}
    />
  );

  return { columns, confirmDialogNode, expandedRowIds };
};

export default useTaskTableColumns;
